import React from 'react';
import { Check, Compass, RefreshCw, X } from '@/utils/icons';
import type { Tutorial } from '@/config/tours';

interface TutorialsMenuProps {
  /** Role/flag-filtered tutorials, in catalog order. */
  tutorials: Tutorial[];
  /** Ids whose launchRoute matches the current screen. */
  currentScreenIds: Set<string>;
  /** Ids the user has completed (or skipped). */
  completedIds: Set<string>;
  onStart: (id: string) => void;
  /** Clears completion so the tutorial can auto-offer / show as new again. */
  onReset: (id: string) => void;
  onClose: () => void;
}

/**
 * Launcher modal listing the guided tutorials available to the current user.
 * Tutorials for the current screen sort to the top with an "On this screen"
 * badge; completed ones get a ✓ and a replay-reset affordance. Uses the shared
 * modal convention (cgm-modal-backdrop + cgm-card cgm-modal-panel).
 */
const TutorialsMenu: React.FC<TutorialsMenuProps> = ({
  tutorials, currentScreenIds, completedIds, onStart, onReset, onClose,
}) => {
  const sorted = [...tutorials].sort((a, b) => {
    const aHere = currentScreenIds.has(a.id) ? 0 : 1;
    const bHere = currentScreenIds.has(b.id) ? 0 : 1;
    return aHere - bHere;
  });

  return (
    <div className="cgm-modal-backdrop" role="dialog" aria-modal="true" aria-label="Tutorials">
      <div className="flex items-center justify-center min-h-screen p-4" onClick={onClose}>
        <div
          className="cgm-card cgm-modal-panel p-8 max-w-lg w-full relative"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 cgm-text-secondary hover:opacity-70 transition-opacity"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="text-center mb-6">
            <h3 className="text-2xl cgm-text-primary mb-2">Tutorials</h3>
            <p className="cgm-text-secondary text-sm">
              Guided walkthroughs of CGM Sports features, right on the screen
            </p>
          </div>

          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {sorted.length === 0 && (
              <p className="cgm-text-secondary text-sm text-center py-4">
                No tutorials available for your account yet.
              </p>
            )}
            {sorted.map((t) => {
              const Icon = t.icon ?? Compass;
              const onThisScreen = currentScreenIds.has(t.id);
              const completed = completedIds.has(t.id);
              // Non-navigable tutorials (parameterized routes) can only start
              // from a matching screen.
              const startable = onThisScreen || t.navigable !== false;
              return (
                <div key={t.id} className="cgm-card p-4 flex gap-3 items-start">
                  <span className="cgm-text-primary shrink-0 mt-0.5" aria-hidden>
                    <Icon className="w-5 h-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="cgm-text-primary font-medium">{t.title}</h4>
                      {completed && (
                        <span
                          className="inline-flex items-center gap-1 text-[11px] text-green-500"
                          title="Completed"
                        >
                          <Check className="w-3.5 h-3.5" /> Done
                        </span>
                      )}
                      {onThisScreen && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-500">
                          On this screen
                        </span>
                      )}
                    </div>
                    <p className="cgm-text-secondary text-sm mt-1">{t.description}</p>
                    {!startable && t.launchHint && (
                      <p className="cgm-text-secondary text-xs mt-1 italic">{t.launchHint}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2.5">
                      <button
                        type="button"
                        onClick={() => onStart(t.id)}
                        disabled={!startable}
                        className="cgm-button-flat py-1.5 px-3 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {completed ? 'Replay' : 'Start'}
                      </button>
                      {completed && (
                        <button
                          type="button"
                          onClick={() => onReset(t.id)}
                          className="inline-flex items-center gap-1 text-xs cgm-text-secondary hover:opacity-70 transition-opacity"
                          title="Clear completion so this tutorial shows as new"
                        >
                          <RefreshCw className="w-3 h-3" /> Reset progress
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <button
            type="button"
            onClick={onClose}
            className="cgm-button w-full mt-6 py-3 px-4 flex items-center justify-center"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TutorialsMenu;
